import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover shadow-md hover:shadow-lg transform hover:-translate-y-0.5 rounded-lg",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg",
        outline:
          "border border-border bg-background hover:bg-muted hover:border-primary text-foreground rounded-lg",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-md hover:shadow-lg rounded-lg",
        ghost: "hover:bg-muted hover:text-foreground rounded-lg",
        link: "text-primary underline-offset-4 hover:underline",
        cta: "bg-primary text-primary-foreground hover:bg-primary-hover shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold rounded-lg",
        "cta-secondary": "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-md hover:shadow-lg font-semibold rounded-lg",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-md hover:shadow-lg rounded-lg",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends VariantProps<typeof buttonVariants>,
    React.ComponentPropsWithoutRef<"button">,
    React.ComponentPropsWithoutRef<"a"> {
  asChild?: boolean
  href?: string
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ className, variant, size, asChild = false, href, ...props }, ref) => {
    const isCtaLink = !asChild && variant === "cta"
    const Comp: any = asChild ? Slot : isCtaLink ? "a" : "button"

    // Default href for CTA buttons — point CTA to HubSpot meetings
    const finalHref = isCtaLink ? href ?? "https://tfimw.share.hsforms.com/2AJnlXqvOTqmdzC_gY5-G3g" : href

    // When rendering an anchor, ensure href is present and set target/rel for external links
    const commonProps: any = {
      className: cn(buttonVariants({ variant, size, className })),
      ref,
      ...(isCtaLink
        ? {
            href: finalHref,
            ...(finalHref && finalHref.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {}),
          }
        : {}),
    }

    return <Comp {...commonProps} {...(props as any)} />
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
